import { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';

import { AUTHENTICATED, NOT_AUTHENTICATED } from '../../design-system/constants';

type LoginStatus = typeof AUTHENTICATED | typeof NOT_AUTHENTICATED;

type AuthState = {
    username: string | null | undefined;
    userId: number | string | null;
    isImpersonated: boolean;
    loginStatus: LoginStatus;
};

type AuthAction =
    | { type: 'LOGIN'; username: string; userId: number | string }
    | { type: 'LOGOUT' }
    | { type: 'MARK_IMPERSONATED' };

export type AuthContextValue = AuthState & {
    isAuthenticated: boolean;
    login: (username: string, userId: number | string) => void;
    logout: () => void;
    markAsImpersonated: () => void;
};

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

type AuthProviderProps = {
    children: ReactNode;
};

const initialState: AuthState = {
    username: null,
    userId: null,
    isImpersonated: false,
    loginStatus: NOT_AUTHENTICATED,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
    switch (action.type) {
        case 'LOGIN':
            return {
                ...state,
                username: action.username,
                userId: action.userId,
                loginStatus: AUTHENTICATED,
            };
        case 'LOGOUT':
            return initialState;
        case 'MARK_IMPERSONATED':
            return {
                ...state,
                isImpersonated: true,
            };
        default:
            return state;
    }
}

export function AuthProvider({ children }: Readonly<AuthProviderProps>): JSX.Element {
    const [state, dispatch] = useReducer(authReducer, initialState);

    const login = useCallback((username: string, userId: number | string) => {
        dispatch({ type: 'LOGIN', username, userId });
    }, []);

    const logout = useCallback(() => {
        dispatch({ type: 'LOGOUT' });
    }, []);

    const markAsImpersonated = useCallback(() => {
        dispatch({ type: 'MARK_IMPERSONATED' });
    }, []);

    return (
        <AuthContext.Provider
            value={{
                ...state,
                isAuthenticated: state.loginStatus === AUTHENTICATED,
                login,
                logout,
                markAsImpersonated,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth(): AuthContextValue {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
